<template>
    <desktop-panel-wrapper show-on-mobile>
        <panel-header
            :title="$t('REVIEW_ORDER')"
            :body="productName"
            :body-subtitle="productSku"
            align="left"
        />

        <p class="cz2__review__copy text-sm">
            {{ $t('REVIEW_COPY') }}
        </p>

        <template v-if="isGroupOrder">
            <div class="cz2__review__totals">
                <div class="cz2__review-totals__actions">
                    <div class="cz2__review-totals__caption">
                        {{ $t('REVIEW_BOTTLES') }}
                    </div>

                    <div class="cz2__review-totals__info">
                        {{ secondaryBottles.length }}
                    </div>
                </div>

                <div class="cz2__review-totals__price">
                    <div class="cz2__review-totals__caption">
                        {{ $t('REVIEW_TOTALS') }}
                    </div>

                    <div class="cz2__review-totals__info">
                        {{ subtotalPrice }}
                        <span v-if="isDiscounted" class="cz2__price--discount">
                            {{ defaultSubtotalPrice }}
                        </span>
                    </div>
                </div>
            </div>
        </template>

        <template v-if="!isGroupOrder">
            <div class="cz2__review__totals">
                <div class="cz2__review-totals__actions">
                    <div class="cz2__review-totals__caption">
                        {{ $t('REVIEW_BOTTLES') }}
                    </div>

                    <div class="cz2__review-totals__info">
                        <number-selector
                            :value="quantity" :min="1"
                            :max="maxQuantity" @select="setQuantity($event)"
                            @decrease="setQuantity(quantity - 1)" @increase="setQuantity(quantity + 1)" />
                    </div>
                </div>

                <div class="cz2__review-totals__price">
                    <div class="cz2__review-totals__caption">
                        {{ $t('REVIEW_TOTALS') }}
                    </div>

                    <div class="cz2__review-totals__info pt-2.5">
                        {{ subtotalPrice }}
                        <span v-if="isDiscounted" class="cz2__price--discount">
                            {{ defaultSubtotalPrice }}
                        </span>
                    </div>
                </div>
            </div>
        </template>

        <div class="flex py-4 space-x-6">
            <button
                v-for="proof in imageSnapshotButtons" :key="proof.name"
                class="flex items-center justify-center"
                :aria-label="proof.name" @click.prevent="download(proof)">
                <img src="../../assets/review-download@2x.png" alt="" width="44px" height="44px" class="border border-gray-300 rounded-full" />
                <div class="uppercase ml-2">{{ proof.name }}</div>
            </button>
        </div>

        <div class="flex flex-col flex-1"></div>
        <primary-button @click="addToCart" class="mb-4">
            {{ actionCaption }}
        </primary-button>
        <secondary-button @click="cancel">
            {{ $t('ACTION_CANCEL') }}
        </secondary-button>
    </desktop-panel-wrapper>
</template>

<style lang="scss">
@import "../../style/variables.scss";

#cz2.cz2 {
    .cz2__review {
        @include respond-below(sm) {
            border-left: none;
        }

        .cz2__panel-header {
            @include respond-below(sm) {
                min-height: 0;
            }
        }
    }

    .cz2__review__close {
        position: absolute;

        left: 20px;
        top: 20px;

        width: 44px;
        height: 44px;

        z-index: 10;

        &:after {
            content: '';

            display: block;
            position: absolute;

            left: 0;
            top: 0;

            width: 44px;
            height: 44px;

            background: transparent url(../../assets/global-close@2x.png) no-repeat center/contain;
        }
    }

    .cz2__review__actions {
        display: none;

        @include respond-above(sm) {
            display: block;
        }
    }

    .cz2__review__copy {
        margin-bottom: 20px;

        @include respond-above(sm) {
            margin-bottom: 40px;
        }
    }

    .cz2__review__menu.cz2__menu__items {
        height: auto;
        display: block;
    }

    .cz2__review__totals {
        display: flex;

        margin-bottom: 20px;

        @include respond-above(sm) {
            margin-top: 20px;
        }
    }

    .cz2__review-totals__actions {
        flex: 1 0 auto;
        width: 1px;

        text-align: left;
    }

    .cz2__review-totals__price {
        flex: 1 0 auto;
        width: 1px;

        text-align: right;
    }

    .cz2__review-totals__caption {
        margin-bottom: 10px;

        font-family: $font-main;
        font-style: normal;
        font-weight: normal;
        font-size: 12px;
        line-height: 16px;
        color: #63666a;
    }

    .cz2__review-totals__info {
        font-family: $font-main;
        font-style: normal;
        font-weight: normal;
        font-size: 24px;
        line-height: 30px;

        .cz2__number-selector {
            max-width: 150px;
        }

        .cz2__price--discount {
            color: #63666a;
            text-decoration: line-through;
            margin-left: 4px;
            display: inline-block;
        }

        .cz2__price--strike {
            display: block;

            margin-top: -5px;

            font-size: 16px;
            text-decoration: line-through;
        }
    }

    .cz2__review__header {
        display: block;

        margin-top: 20px;
        margin-bottom: 20px;

        .cz2__product-title {
            white-space: normal;
            width: auto;
            margin-bottom: 5px;
        }
    }
}
</style>

<script>

    import axios from 'axios';
    import FileSaver from 'file-saver';
    import Cookies from 'js-cookie';
    import { mapGetters, mapState } from 'vuex';
    import SecondaryButton from '../buttons/SecondaryButton.vue';

    import PanelHeader from '../PanelHeader.vue';
    import NumberSelector from '../NumberSelector.vue';

    import FullScreenPanel from './FullScreenPanel.vue';
    import FullScreenPanelContent from './FullScreenPanelContent.vue';
    import { getImageSnapshotButtons } from '../../utilities/UI/buttons';
    import DesktopPanelWrapper from './desktop/DesktopPanelWrapper.vue';
    import PrimaryButton from '../buttons/PrimaryButton.vue';
    import { fillEmptyDivs } from '../../utils';

    export default {
        components: {
            DesktopPanelWrapper,
            PanelHeader,
            NumberSelector,
            FullScreenPanel,
            FullScreenPanelContent,
            PrimaryButton,
            SecondaryButton,
        },
        data() {
            return {
                savedRecipe: null,
            };
        },
        computed: {
            ...mapState({
                viewController: (state) => state.customizer.viewController,
                isGroupOrder: (state) => state.isGroupOrder,
                selectedBottle: (state) => state.customizer.selectedBottle,
                secondaryBottles: (state) => state.customizer.secondaryBottles,
                recipe: (state) => state.customizer.recipe,
                subtotalPrice: (state) => state.customizer.subtotalPrice,
                defaultSubtotalPrice: (state) => state.customizer.defaultSubtotalPrice,
                quantity: (state) => state.customizer.quantity,
                blueprintViews: (state) => state.customizer.viewController.blueprint.views,
            }),

            ...mapGetters({
                /* Can we build this configuration? */
                canBuild: 'canBuild',
                selectedColorHex: 'selectedColorHex',
                maxQuantity: 'availableQuantity',
            }),

            isDiscounted() {
                return this.subtotalPrice !== this.defaultSubtotalPrice;
            },

            actionCaption() {
                return this.recipe
                    ? this.$t('ACTION_UPDATE')
                    : this.$t('ACTION_ADD_TO_CART');
            },

            imageSnapshotButtons() {
                return getImageSnapshotButtons({
                    viewController: this.viewController,
                });
            },

            selectedBottleIndex() {
                return (this.selectedBottle && this.secondaryBottles)
                    ? this.secondaryBottles.indexOf(this.selectedBottle)
                    : -1;
            },

            shareTitle() {
                return navigator.share
                    ? this.$t('REVIEW_SHARE')
                    : this.$t('REVIEW_EMAIL');
            },

            productName() {
                return this.viewController.blueprint.name;
            },

            productSku() {
                const selected = this.viewController.selectedAtPlacement('Product');

                return selected == null ? null : selected.custom['v-sku'];
            },
        },
        watch: {
            savedRecipe() {
                let url = new window.URL(window.location.href);

                url.searchParams.delete('edit');
                url.searchParams.append('edit', this.savedRecipe.recipe.id);

                url = url.toString();

                window.history.replaceState(null, null, url);
            },
        },
        mounted() {
            // Save a recipe here to be able to share synchronously later
            this.$store.dispatch('customizerDone', {
                saveOnly: true,
                noBusy: true,
            }).then((recipe) => {
                this.savedRecipe = recipe;
            });
            fillEmptyDivs();
        },
        methods: {
            addToCart() {
                // Are bottles customized with different text?
                if (this.isGroupOrder) {
                    const bottles = this.secondaryBottles;

                    const uniqueText = new Set();
                    let haveEmpty = false;

                    if (bottles) {
                        bottles.forEach((bottle) => {
                            const text = bottle.map((x) => (x || '').trim()).join('|');

                            if (text.replace(/\|/g, '').length === 0) {
                                haveEmpty = true;
                            }

                            uniqueText.add(text);
                        });
                    }

                    const uniqueCount = uniqueText.size;

                    if (uniqueCount < 2 || haveEmpty) {
                        this.$store.dispatch('showGenericConfirm', {
                            title: this.$t('NOGROUP_TITLE'),
                            description: this.$t('NOGROUP_BODY'),
                            confirmAction: this.$t('NOGROUP_ACCEPT'),
                            cancelAction: this.$t('NOGROUP_CANCEL'),
                            confirmationCallback: () => {
                                this.$store.dispatch('setIsGroupOrder', false);
                            },
                        });

                        // Exit regardless.
                        return;
                    }
                }

                let actualFilledTextPlacements = this.$store.getters.filledTextPlacements.length;

                if (!this.isGroupOrder) {
                    actualFilledTextPlacements = 0;

                    this.$store.getters.textPlacements.forEach((c) => {
                        if (c.component.custom
                            && c.component.custom['personalization-text']
                            && c.component.custom['personalization-text'].trim().length > 0) {
                            actualFilledTextPlacements += 1;
                        }
                    });
                }

                if (actualFilledTextPlacements === 0
                    && this.$store.getters.filledMonogramPlacements.length === 0
                    && this.$store.getters.filledImagePlacements.length === 0) {
                    this.$store.dispatch('showGenericNotify', {
                        title: this.$t('NO_PERSONALIZATIONS'),
                        description: this.$t('NO_PERSONALIZATIONS_BODY'),
                        action: this.$t('ACTION_CONTINUE'),
                    });
                } else if (this.canBuild) {
                    // if (Cookies.get('CUSTOM-TOS') === 'accepted') {
                    //     this.$store.dispatch('customizerDone');
                    // } else {

                    // Show confirmation every time.
                    this.$store.dispatch('showTosConfirmation', true);
                    // }
                }
            },

            /**
             * Select a step.
             */
            selectStep(step, returnTo) {
                if (returnTo) {
                    this.$store.dispatch('selectStep', {
                        step,
                        parameters: {
                            returnTo,
                        },
                    });
                } else {
                    this.$store.dispatch('selectStep', step);
                }
            },

            /**
             * Go back.
             */
            cancel() {
                // Go back to the main screen.
                this.$store.dispatch('selectStep', null);
            },

            setQuantity(v) {
                this.$store.dispatch('setQuantity', v);
            },

            saveWishlist() {
                // Need a recipe to share it
                if (!this.savedRecipe) {
                    return;
                }

                this.$store.dispatch('saveWishlist', {
                    id: this.savedRecipe.recipe.id,
                });
            },

            download(view) {
                const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

                if (isIos) {
                    window.open(view.url);
                } else {
                    axios.get(view.url, { responseType: 'blob' }).then((response) => {
                        let name;

                        if (this.isGroupOrder) {
                            name = `Proof-${this.selectedBottleIndex}-${view.name}.jpg`;
                        } else {
                            name = `Proof-${view.name}.jpg`;
                        }

                        FileSaver.saveAs(response.data, name);
                    });
                }
            },

            share() {
                // Need a recipe to share it
                if (!this.savedRecipe) {
                    return;
                }

                let url = new window.URL(window.location.href);

                url.searchParams.delete('edit');
                url.searchParams.append('edit', this.savedRecipe.recipe.id);

                url = url.toString();

                if (navigator.share) {
                    navigator.share({
                        url,
                    });
                } else {
                    window.location.href = `mailto:?body=${encodeURIComponent(url)}`;
                }
            },

            help() {
                this.$store.dispatch('selectStep', {
                    step: 'help',
                    parameters: {
                        returnTo: 'review',
                    },
                });
            },
        },
    };
</script>
